<script lang="ts">
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { AqueductSession } from '$lib/aqueduct/engine/session';
	import { isWebGpuError } from '$lib/aqueduct/webGpuSupport';
	import type { ProjectHandle } from '$lib/aqueduct/engine/projectHandle';
	import type { SurfaceKind } from '$lib/aqueduct/engine/protocol';
	import { demoTimeWarp } from '$lib/aqueduct/engine/demoClock';
	import {
		getDefaultMaterialGridSsaaScale,
		getMaterialViewportDpr,
		type MaterialGridSsaaScale
	} from '$lib/surfaceDpr';
	import LandingPoster from '$components/landing/LandingPoster.svelte';
	import { DEFAULT_MATERIAL_DISPLACEMENT_SCALE } from '$lib/aqueduct/materialRendererDefaults';

	const dispatch = createEventDispatcher<{
		ready: void;
		unavailable: void;
		failed: { error: string };
	}>();

	type Status = 'idle' | 'loading' | 'ready' | 'error' | 'no-webgpu';

	const DEMO_GRAPH_PATH = '/aqueduct/examples/demo/aqueduct-landing-demo-graph.aqueduct';

	// On a document load of '/', app.html prefetches this same graph at first
	// byte (path picked by PUBLIC_SITE). On a client-side navigation back to
	// '/', app.html never re-ran, so the component fetches for itself.
	type LandingWindow = Window &
		typeof globalThis & {
			__aqueductLandingResources?: { startedAt: number; graphToml: Promise<string> };
		};

	// Every art-direction knob the Rust scene exposes; tuned live via ?tuner.
	const DEFAULT_CONFIG = {
		bloomScale: 0.5,
		bloomSigmaCssPx: 4.0,
		bloomOpacity: 0.3,
		displacementScale: DEFAULT_MATERIAL_DISPLACEMENT_SCALE,
		dropletSizeFrac: 0.66,
		dropletTopFrac: 0.02,
		waterLineFrac: 0.74,
		bobAmpCssPx: 4.0,
		bobPeriodS: 8.0,
		spinSpeed: 0.25,
		waveAmpCssPx: 5.0,
		waveWavelengthCssPx: 46.0,
		wavePeriodS: 4.0,
		waveDecayCssPx: 130.0,
		poolOpacity: 0.85,
		poolRadiusFrac: 0.32,
		envBrightness: 1.0,
		fresnelF0: 0.04,
		waterBrightness: 0.8,
		cameraElevationDeg: 12,
		waveCenterSoftPx: 35,
		poolTaper: 4.0
	};
	type DropletConfig = typeof DEFAULT_CONFIG;
	type ConfigKey = keyof DropletConfig;

	type TunerControl = {
		key: ConfigKey;
		label: string;
		min: number;
		max: number;
		step: number;
		tab: string;
	};
	const TUNER_CONTROLS: TunerControl[] = [
		{ key: 'bloomSigmaCssPx', label: 'Sigma px', min: 0, max: 16, step: 0.25, tab: 'bloom' },
		{ key: 'bloomOpacity', label: 'Opacity', min: 0, max: 1, step: 0.01, tab: 'bloom' },
		{ key: 'dropletSizeFrac', label: 'Size', min: 0.2, max: 1, step: 0.01, tab: 'droplet' },
		{ key: 'dropletTopFrac', label: 'Top', min: 0, max: 0.4, step: 0.01, tab: 'droplet' },
		{ key: 'spinSpeed', label: 'Spin', min: 0, max: 1.5, step: 0.01, tab: 'droplet' },
		{ key: 'bobAmpCssPx', label: 'Bob px', min: 0, max: 20, step: 0.5, tab: 'droplet' },
		{ key: 'bobPeriodS', label: 'Bob s', min: 1, max: 15, step: 0.5, tab: 'droplet' },
		{ key: 'displacementScale', label: 'Displace', min: 0, max: 0.05, step: 0.001, tab: 'droplet' },
		{ key: 'cameraElevationDeg', label: 'Elevation deg', min: 0, max: 80, step: 1, tab: 'droplet' },
		{ key: 'waveAmpCssPx', label: 'Amp px', min: 0, max: 24, step: 0.5, tab: 'wave' },
		{ key: 'waveWavelengthCssPx', label: 'Length px', min: 8, max: 160, step: 1, tab: 'wave' },
		{ key: 'wavePeriodS', label: 'Period s', min: 0.3, max: 8, step: 0.1, tab: 'wave' },
		{ key: 'waveDecayCssPx', label: 'Decay px', min: 20, max: 600, step: 5, tab: 'wave' },
		{ key: 'waveCenterSoftPx', label: 'Center soft px', min: 0, max: 60, step: 1, tab: 'wave' },
		{ key: 'waterLineFrac', label: 'Water line', min: 0.4, max: 1.2, step: 0.01, tab: 'pool' },
		{ key: 'poolOpacity', label: 'Opacity', min: 0, max: 1, step: 0.01, tab: 'pool' },
		{ key: 'poolRadiusFrac', label: 'Radius', min: 0.05, max: 1, step: 0.01, tab: 'pool' },
		{ key: 'envBrightness', label: 'Env', min: 0, max: 3, step: 0.05, tab: 'pool' },
		{ key: 'fresnelF0', label: 'Fresnel F0', min: 0, max: 0.3, step: 0.005, tab: 'pool' },
		{ key: 'waterBrightness', label: 'Water base', min: 0, max: 2, step: 0.05, tab: 'pool' },
		{ key: 'poolTaper', label: 'Taper', min: 0, max: 6, step: 0.1, tab: 'pool' }
	];
	const TUNER_TABS = ['bloom', 'droplet', 'wave', 'pool'];

	let status = $state<Status>('idle');
	let containerEl: HTMLDivElement;
	let canvasEl: HTMLCanvasElement;
	let session: AqueductSession | null = null;
	let project = $state<ProjectHandle | null>(null);
	// The poster is the engine's own t=0 frame, captured by
	// scripts/capture-landing-posters.mjs and served statically. It is the
	// first paint of the hero; the live canvas replaces it only after its
	// first frame (also t=0, anchored by the worker) has actually presented,
	// so the swap is pixel-identical and the animation appears to start from
	// the poster. It also stays up permanently when WebGPU is unavailable.
	let showPoster = $state(true);
	// Epoch for the hit-test mirror of the worker's demo clock: anchored
	// when the release (demoStart) is sent, null while the clock is held.
	// The sub-frame offset from the worker's own origin is within the pad.
	let timeOriginMs: number | null = null;
	function demoNowMs(): number {
		// Hit-test mirror of the worker's demo clock (engine/demoClock.ts is
		// the single definition of demo time): before release the clock is
		// held at exactly 0; after, the same warp applies against the epoch
		// anchored when the release was sent (sub-frame from the worker's).
		if (timeOriginMs === null) return 0;
		return demoTimeWarp(performance.now() - timeOriginMs);
	}
	// ?poster: capture mode for the poster script. The worker renders exactly
	// one frame pinned to t=0 and never starts the animation loop.
	let posterCaptureMode: boolean = $state(false);
	let config = $state<DropletConfig>({ ...DEFAULT_CONFIG });
	let tunerVisible = $state(false);
	let tunerTab = $state('wave');
	let resizeObserver: ResizeObserver | null = null;
	let themeObserver: MutationObserver | null = null;
	let unsubscribe: (() => void) | null = null;
	let isRegistered = false;
	let presentedHandled = false;
	let lastCssWidth = 0;
	let lastCssHeight = 0;
	let lastDpr = 1;
	let lastMaterialGridSsaaScale: MaterialGridSsaaScale = 4;
	let hoverDroplet = $state(false);
	let draggingDroplet = $state(false);
	let destroyed: boolean = false;

	function currentDark(): boolean {
		return document.documentElement.classList.contains('dark');
	}

	type SurfaceMetrics = {
		cssWidth: number;
		cssHeight: number;
		dpr: number;
		materialGridSsaaScale: MaterialGridSsaaScale;
	};

	function surfaceMetrics(): SurfaceMetrics | null {
		if (!canvasEl || !containerEl) return null;
		const rect = containerEl.getBoundingClientRect();
		const dpr = getMaterialViewportDpr();
		return {
			cssWidth: rect.width,
			cssHeight: rect.height,
			dpr,
			materialGridSsaaScale: getDefaultMaterialGridSsaaScale(dpr)
		};
	}

	function onResize(): void {
		if (!isRegistered || !project) return;
		const metrics = surfaceMetrics();
		if (!metrics) return;
		if (
			metrics.cssWidth === lastCssWidth &&
			metrics.cssHeight === lastCssHeight &&
			metrics.dpr === lastDpr &&
			metrics.materialGridSsaaScale === lastMaterialGridSsaaScale
		)
			return;
		lastCssWidth = metrics.cssWidth;
		lastCssHeight = metrics.cssHeight;
		lastDpr = metrics.dpr;
		lastMaterialGridSsaaScale = metrics.materialGridSsaaScale;
		project.resizeSurface(
			'dropletDemo',
			metrics.cssWidth,
			metrics.cssHeight,
			metrics.dpr,
			undefined,
			[metrics.materialGridSsaaScale]
		);
	}

	function applyConfig(targetProject: ProjectHandle | null = project): void {
		targetProject?.demoConfig('dropletDemo', JSON.stringify(config));
	}

	function handleTunerInput(key: ConfigKey, event: Event): void {
		const input = event.currentTarget as HTMLInputElement | null;
		if (!input) return;
		const value = input.valueAsNumber;
		if (!Number.isFinite(value)) return;
		config = { ...config, [key]: value };
		applyConfig();
	}

	function resetTunerTab(): void {
		const next = { ...config };
		for (const control of TUNER_CONTROLS) {
			if (control.tab === tunerTab) next[control.key] = DEFAULT_CONFIG[control.key];
		}
		config = next;
		applyConfig();
	}

	function copyTunerConfig(): void {
		void navigator.clipboard?.writeText(JSON.stringify(config, null, 2));
	}

	function stopTunerPointer(event: PointerEvent): void {
		event.stopPropagation();
	}

	function canvasPoint(event: PointerEvent): { x: number; y: number } {
		const rect = canvasEl.getBoundingClientRect();
		return { x: event.clientX - rect.left, y: event.clientY - rect.top };
	}

	// Mirror of the Rust-side exact-silhouette hit test, for the cursor and
	// the capture decision only: the engine's grabbed-bool return does not
	// cross the worker boundary, and it ignores moves when not grabbed, so
	// always forwarding pointer events stays correct. Downsampled copy of the
	// engine's digitized TEARDROP_PROFILE (half-width by height, tip to base).
	const DROPLET_PROFILE = [
		0.0, 0.0414, 0.0655, 0.0867, 0.1075, 0.129, 0.1517, 0.1757, 0.2011, 0.2277, 0.2553, 0.2836,
		0.3124, 0.3412, 0.3699, 0.3981, 0.4255, 0.452, 0.4773, 0.5012, 0.5236, 0.5444, 0.5634, 0.5807,
		0.5961, 0.6096, 0.6213, 0.631, 0.6389, 0.6449, 0.6489, 0.6509, 0.6509, 0.6488, 0.6444, 0.6375,
		0.6279, 0.6153, 0.5994, 0.5796, 0.5555, 0.5263, 0.4911, 0.4486, 0.3967, 0.3317, 0.2435, 0.0
	];
	function dropletHit(x: number, y: number): boolean {
		const rect = containerEl?.getBoundingClientRect();
		if (!rect) return false;
		const V_TOP = 0.22;
		const V_BOT = 0.78;
		const size = Math.min(rect.width * config.dropletSizeFrac, rect.height * 0.62);
		const left = (rect.width - size) / 2;
		const bob =
			Math.sin(((demoNowMs() / 1000) * 2 * Math.PI) / Math.max(config.bobPeriodS, 0.001)) *
			config.bobAmpCssPx;
		const top = rect.height * config.dropletTopFrac + bob;
		const u = (x - left) / size;
		const v = (y - top) / size;
		const pad = 6 / Math.max(size, 1);
		if (v < V_TOP - pad || v > V_BOT + pad) return false;
		const tn = Math.min(1, Math.max(0, (v - V_TOP) / (V_BOT - V_TOP)));
		const f = tn * (DROPLET_PROFILE.length - 1);
		const i = Math.min(Math.floor(f), DROPLET_PROFILE.length - 2);
		const halfW = DROPLET_PROFILE[i] * (1 - (f - i)) + DROPLET_PROFILE[i + 1] * (f - i);
		return Math.abs(u - 0.5) <= (halfW * (V_BOT - V_TOP)) / 2 + pad;
	}

	// Pointer events forward fire-and-forget; the worker stamps them with
	// its demo clock on arrival (main-thread timestamps have a different
	// origin and must not cross the boundary).
	// Interactivity begins at release (timeOriginMs is anchored when
	// demoStart is sent): before that, frames are held and any forwarded
	// pointer event would bank invisible simulation state that bursts out
	// all at once when rendering resumes. Until then: no grab cursor, no
	// hit-test, no events into the sim.
	function interactive(): boolean {
		return timeOriginMs !== null;
	}

	function handlePointerDown(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive()) return;
		const point = canvasPoint(event);
		project.demoPointer('dropletDemo', 0, point.x, point.y);
		const dragging = dropletHit(point.x, point.y);
		draggingDroplet = dragging;
		if (dragging) {
			event.preventDefault();
			canvasEl.setPointerCapture(event.pointerId);
		}
	}

	function handlePointerMove(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive()) return;
		const point = canvasPoint(event);
		hoverDroplet = dropletHit(point.x, point.y);
		project.demoPointer('dropletDemo', 1, point.x, point.y);
	}

	function handlePointerUp(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive()) return;
		if (canvasEl.hasPointerCapture(event.pointerId))
			canvasEl.releasePointerCapture(event.pointerId);
		draggingDroplet = false;
		const point = canvasPoint(event);
		project.demoPointer('dropletDemo', 2, point.x, point.y);
	}

	function handleInitError(error: unknown): void {
		if (destroyed) return;
		const message = String(error);
		if (posterCaptureMode) {
			(window as Window & { __dropletPosterError?: string }).__dropletPosterError = message;
		}
		if (isWebGpuError(error)) {
			status = 'no-webgpu';
			dispatch('unavailable');
		} else {
			status = 'error';
			console.error('AqueductDropletDemo init failed:', error);
			dispatch('failed', { error: message });
		}
	}

	function handlePresented(kinds: SurfaceKind[]): void {
		if (destroyed || presentedHandled || !kinds.includes('dropletDemo')) return;
		presentedHandled = true;
		if (posterCaptureMode) {
			// Held t=0 frame is on screen; signal the capture script after the
			// compositor has shown it (double rAF).
			showPoster = false;
			requestAnimationFrame(() =>
				requestAnimationFrame(() => {
					(window as Window & { __dropletPosterReady?: boolean }).__dropletPosterReady = true;
				})
			);
			return;
		}
		dispatch('ready');
		// Atomic swap, after the first canvas frame has actually presented
		// (double rAF): the canvas's CSS opacity is gated on !showPoster, so
		// the poster leaves and the canvas appears in the same frame. The
		// two layers never overlap; overlapping them double-composites
		// every semi-transparent pixel (pool, glow) and reads as a flash.
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				showPoster = false;
				// The worker holds a single rendered t=0 frame (the poster
				// pose) until released, so the swap cannot skip animation
				// time. Release after a double rAF: rAF callbacks run before
				// their frame commits, so a single rAF races the swap into
				// the compositor and the seam shows motion already underway.
				// The worker stamps the clock origin at its next frame tick,
				// so the first moving frame is exactly t=0.
				requestAnimationFrame(() =>
					requestAnimationFrame(() => {
						if (destroyed) return;
						timeOriginMs = performance.now();
						project?.demoStart();
					})
				);
			})
		);
	}

	async function initEngine(): Promise<void> {
		if (!navigator.gpu) {
			status = 'no-webgpu';
			dispatch('unavailable');
			return;
		}
		try {
			status = 'loading';
			const early = (window as LandingWindow).__aqueductLandingResources;
			const graphToml = await (early
				? early.graphToml
				: fetch(DEMO_GRAPH_PATH).then((r) => r.text()));
			if (destroyed) return;
			session = new AqueductSession({
				docId: 'aqueduct-landing-demo',
				clientId: 'aqueduct-landing-demo',
				warmSlangCompiler: false
			});
			// Engine boot failures (worker, wasm, GPU init) surface here; the
			// per-call commands below are fire-and-forget and cannot report them.
			session.booted.catch((error) => handleInitError(error));
			// Synchronous: commands buffer FIFO behind engine boot in the worker,
			// so no boot-ordering ceremony is needed on this thread.
			const handle = session.project;
			project = handle;
			unsubscribe = session.subscribe({
				onPresented: handlePresented,
				onRenderError: (message: string) => {
					status = 'error';
					console.error('Aqueduct droplet demo render failed:', message);
					dispatch('failed', { error: message });
				}
			});
			// Poster capture is the exact t=0 frame: the animated graph must also
			// evaluate at t=0, independent of how long the page took to load.
			if (posterCaptureMode) handle.setTimeOverride(0);
			session.loadGraphToml(graphToml);
			// ?profile: per-frame stage timings in the console, same flag and
			// format family as the iliad.ai landing sphere demo.
			if (new URLSearchParams(window.location.search).has('profile')) {
				handle.setLogConfig(JSON.stringify({ landingFrameProfiling: true }));
			}

			const metrics = surfaceMetrics();
			if (!metrics) throw new Error('Droplet demo canvas could not be sized');
			lastCssWidth = metrics.cssWidth;
			lastCssHeight = metrics.cssHeight;
			lastDpr = metrics.dpr;
			lastMaterialGridSsaaScale = metrics.materialGridSsaaScale;
			// The worker creates each graph, material, and droplet presentation
			// pipeline as a separate cold-start unit, yielding a browser
			// presentation opportunity between units. It does not inspect or wait
			// for input/scroll idleness; preparation advances continuously while
			// this pixel-matched poster remains visible.
			await handle.prepareStartupPipelines('landingDroplet');
			if (destroyed) return;
			// Backing store is sized before the transfer; afterwards only the
			// worker may touch canvas dimensions.
			canvasEl.width = Math.max(1, Math.round(metrics.cssWidth * metrics.dpr));
			canvasEl.height = Math.max(1, Math.round(metrics.cssHeight * metrics.dpr));
			handle.attachCanvas(
				{
					kind: 'dropletDemo',
					width: metrics.cssWidth,
					height: metrics.cssHeight,
					dpr: metrics.dpr,
					extra: [metrics.materialGridSsaaScale]
				},
				canvasEl
			);
			isRegistered = true;
			applyConfig(handle);
			const darkMode = currentDark();
			// The worker owns the frame loop and anchors t=0 at its first frame,
			// matching the poster capture. ?poster renders exactly one frame
			// pinned to t=0 and does not loop.
			handle.demoLoop(
				'dropletDemo',
				posterCaptureMode ? { darkMode, posterFrame: true } : { darkMode }
			);
			status = 'ready';
		} catch (error) {
			handleInitError(error);
		}
	}

	onMount(() => {
		const params = new URLSearchParams(window.location.search);
		tunerVisible = params.has('tuner');
		posterCaptureMode = params.has('poster');
		const brandAsset: string | null = params.get('brandAsset');
		if (brandAsset === 'droplet-only' || brandAsset === 'droplet-only-no-displacement') {
			// Official brand capture: preserve the exact deterministic t=0
			// droplet render while removing the separately composited pool.
			config = { ...DEFAULT_CONFIG, poolOpacity: 0 };
			if (brandAsset === 'droplet-only-no-displacement') config.displacementScale = 0;
		}
		void initEngine();
		resizeObserver = new ResizeObserver(() => onResize());
		resizeObserver.observe(containerEl);
		// The theme toggle flips the <html> class without any prop update here;
		// re-sending the loop config updates darkness without resetting the
		// worker's demo clock.
		themeObserver = new MutationObserver(() => {
			if (isRegistered && project && !posterCaptureMode) {
				project.demoLoop('dropletDemo', { darkMode: currentDark() });
			}
		});
		themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class']
		});
	});

	onDestroy(() => {
		destroyed = true;
		resizeObserver?.disconnect();
		resizeObserver = null;
		themeObserver?.disconnect();
		themeObserver = null;
		unsubscribe?.();
		unsubscribe = null;
		session?.close();
		session = null;
		project = null;
		isRegistered = false;
	});
</script>

<div bind:this={containerEl} class="relative h-full w-full select-none">
	<canvas
		bind:this={canvasEl}
		class="aqueduct-droplet-demo-canvas absolute inset-0 block h-full w-full touch-pan-y"
		style="opacity: {status === 'ready' && !showPoster ? 1 : 0}; cursor: {draggingDroplet
			? 'grabbing'
			: hoverDroplet
				? 'grab'
				: 'default'};"
		onpointerdown={handlePointerDown}
		onpointermove={handlePointerMove}
		onpointerleave={() => (hoverDroplet = false)}
		onpointerup={handlePointerUp}
		onpointercancel={handlePointerUp}
	></canvas>
	{#if showPoster && !posterCaptureMode}
		<!-- Engine-rendered t=0 posters (light/dark, transparent), shown from
		     first paint until the atomic swap, at the resolution the canvas
		     will render (LandingPoster). The canvas stays at opacity 0 the
		     whole time the poster is up. pointer-events-none so the canvas
		     owns interaction throughout. -->
		<LandingPoster
			base="/landing/droplet-poster-light"
			imgClass="pointer-events-none absolute inset-0 h-full w-full dark:hidden"
		/>
		<LandingPoster
			base="/landing/droplet-poster-dark"
			imgClass="pointer-events-none absolute inset-0 hidden h-full w-full dark:block"
		/>
	{/if}

	{#if tunerVisible}
		<div
			class="fixed right-3 top-3 z-50 grid max-h-[calc(100vh-1.5rem)] w-[300px] gap-2 overflow-y-auto rounded border border-white/15 bg-zinc-950/95 p-2 text-[10px] leading-none text-zinc-300 shadow-xl backdrop-blur"
			role="group"
			onpointerdown={stopTunerPointer}
			onpointermove={stopTunerPointer}
			onpointerup={stopTunerPointer}
			onpointercancel={stopTunerPointer}
		>
			<div class="grid grid-cols-[1fr_auto_auto] items-center gap-2">
				<div class="grid grid-cols-4 gap-1">
					{#each TUNER_TABS as tab (tab)}
						<button
							type="button"
							class="h-5 rounded border px-1 font-medium {tunerTab === tab
								? 'border-cyan-300/60 bg-cyan-300/15 text-cyan-100'
								: 'border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-100'}"
							onclick={() => (tunerTab = tab)}
						>
							{tab}
						</button>
					{/each}
				</div>
				<button
					type="button"
					class="h-5 rounded border border-white/15 px-1.5 font-medium text-zinc-300 hover:border-white/30 hover:text-white"
					onclick={copyTunerConfig}
				>
					Copy
				</button>
				<button
					type="button"
					class="h-5 rounded border border-white/15 px-1.5 font-medium text-zinc-300 hover:border-white/30 hover:text-white"
					onclick={resetTunerTab}
				>
					Reset
				</button>
			</div>
			{#each TUNER_CONTROLS.filter((control) => control.tab === tunerTab) as control (control.key)}
				<label class="grid grid-cols-[64px_1fr_40px] items-center gap-2">
					<span class="text-zinc-400">{control.label}</span>
					<input
						type="range"
						min={control.min}
						max={control.max}
						step={control.step}
						value={config[control.key]}
						oninput={(event) => handleTunerInput(control.key, event)}
					/>
					<span class="text-right tabular-nums">{config[control.key].toFixed(2)}</span>
				</label>
			{/each}
		</div>
	{/if}
</div>
